import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import DashboardLayout from "./pages/dashboard/DashboardLayout";

// Role dashboards
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import StudentDashboard from "./pages/dashboard/StudentDashboard";
import FacultyDashboard from "./pages/dashboard/FacultyDashboard";
import ParentDashboard from "./pages/dashboard/ParentDashboard";
import HRDashboard from "./pages/dashboard/HRDashboard";
import PlacementDashboard from "./pages/dashboard/PlacementDashboard";

// Modules
import Admissions from "./pages/modules/Admissions";
import Fees from "./pages/modules/Fees";
import Attendance from "./pages/modules/Attendance";
import LMS from "./pages/modules/LMS";
import Examinations from "./pages/modules/Examinations";
import HRModule from "./pages/modules/HR";
import Payroll from "./pages/modules/Payroll";
import PlacementModule from "./pages/modules/Placement";
import OBEReports from "./pages/modules/OBEReports";
import Certificates from "./pages/modules/Certificates";
import Communications from "./pages/modules/Communications";
import Library from "./pages/modules/Library";
import Results from "./pages/modules/Results";
import Profile from "./pages/modules/Profile";
import Timetable from "./pages/modules/Timetable";
import MentorMentee from "./pages/modules/MentorMentee";
import WhatsAppCenter from "./pages/modules/WhatsAppCenter";

// Feature pages (public, SEO-indexable)
import FeaturePage, { FeaturesIndex } from "./pages/features/FeaturePage";
import Contact from "./pages/Contact";
import DocsLayout from "./pages/docs/DocsLayout";

const ROLE_DASHBOARDS = {
  admin: <AdminDashboard />,
  student: <StudentDashboard />,
  faculty: <FacultyDashboard />,
  parent: <ParentDashboard />,
  hr_manager: <HRDashboard />,
  placement_officer: <PlacementDashboard />,
};

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F2EA]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 bg-[#0C0C10] animate-pulse" />
        <p className="font-['Syne'] text-xs uppercase tracking-widest text-[#7A7468]">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleDashboard() {
  const { user } = useAuth();
  return ROLE_DASHBOARDS[user?.role] || <AdminDashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<FeaturesIndex />} />
            <Route path="/features/:slug" element={<FeaturePage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/docs/*" element={<DocsLayout />} />
            <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
            <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<RoleDashboard />} />
              <Route path="admissions" element={<Admissions />} />
              <Route path="fees" element={<Fees />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="lms" element={<LMS />} />
              <Route path="exams" element={<Examinations />} />
              <Route path="results" element={<Results />} />
              <Route path="hr" element={<HRModule />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="placement" element={<PlacementModule />} />
              <Route path="obe" element={<OBEReports />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="comms" element={<Communications />} />
              <Route path="library" element={<Library />} />
              <Route path="reports" element={<OBEReports />} />
              <Route path="timetable" element={<Timetable />} />
              <Route path="mentor" element={<MentorMentee />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Profile />} />
              <Route path="whatsapp" element={<WhatsAppCenter />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
